"use client";
import { Link } from "react-router-dom";
import { Linkedin } from "lucide-react";
import { useTranslation } from "react-i18next";
import BrandLogo from "@/components/BrandLogo";

const IONOS_PARTNER_URL =
  "https://partnernetwork.cloud.ionos.fr/partner/eruditego?origin=PartnerBadge";
const IONOS_BADGE_SRC =
  "https://images-1.partnerportal.cloud.ionos.fr/items/5607abbe-4ca7-4169-842b-f00b977f2e2e/profiles/5845c93d-7923-4989-b910-62bb13d00c7f/badges/normal_white_eco";

const Footer = () => {
  const { t } = useTranslation();

  return (
    <footer className="bg-navy text-navy-foreground py-12">
      <div className="container mx-auto px-4 md:px-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
          <div>
            <div className="mb-3">
              <BrandLogo variant="lockup" onDark imgClassName="h-7 w-7" className="text-sm" />
            </div>
            <p className="text-xs opacity-60 leading-relaxed">
              {String(t("footer.description"))}
            </p>
          </div>
          <div>
            <h4 className="font-display font-semibold text-xs uppercase tracking-wider mb-3 opacity-70">
              {String(t("footer.infrastructure"))}
            </h4>
            <p className="text-xs opacity-60 leading-relaxed">
              {String(t("footer.infrastructureDesc"))}
            </p>
          </div>
          <div>
            <h4 className="font-display font-semibold text-xs uppercase tracking-wider mb-3 opacity-70">
              {String(t("footer.contact"))}
            </h4>
            <p className="text-xs opacity-60 leading-relaxed">
              {String(t("footer.contactDesc"))}
              <br />
              sales@resilience365.eu
            </p>
            <div className="flex flex-col gap-1.5 text-xs opacity-60">
              <a
                href="https://www.linkedin.com/showcase/resilience365"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-100 transition-opacity inline-flex items-center gap-1"
              >
                <Linkedin className="h-3 w-3" /> LinkedIn
              </a>
            </div>
          </div>
          <div>
            <h4 className="font-display font-semibold text-xs uppercase tracking-wider mb-3 opacity-70">
              {String(t("footer.legal"))}
            </h4>
            <div className="flex flex-col gap-1.5 text-xs opacity-60">
              <Link to="/privacy" className="hover:opacity-100 transition-opacity">
                {String(t("footer.privacyPolicy"))}
              </Link>
              <Link to="/dpa" className="hover:opacity-100 transition-opacity">
                {String(t("footer.dpa"))}
              </Link>
              <Link to="/terms" className="hover:opacity-100 transition-opacity">
                {String(t("footer.termsOfService"))}
              </Link>
              <Link to="/cookies" className="hover:opacity-100 transition-opacity">
                {String(t("footer.cookiePolicy"))}
              </Link>
              <Link to="/aup" className="hover:opacity-100 transition-opacity">
                {String(t("footer.acceptableUsePolicy"))}
              </Link>
              <Link to="/sla" className="hover:opacity-100 transition-opacity">
                {String(t("footer.sla"))}
              </Link>
            </div>
          </div>
        </div>

        {/* IONOS Cloud partnership */}
        <div className="border-t border-border/20 pt-8 pb-2">
          <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10 rounded-xl border border-white/10 bg-white/[0.03] px-5 py-5 md:px-7 md:py-6">
            <a
              href={IONOS_PARTNER_URL}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="shrink-0 inline-flex self-start md:self-center opacity-95 hover:opacity-100 transition-opacity"
            >
              <img
                src={IONOS_BADGE_SRC}
                alt={String(t("footer.ionos.badgeAlt"))}
                className="h-14 sm:h-16 w-auto"
                loading="lazy"
                decoding="async"
              />
            </a>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-wider text-accent mb-1.5">
                {String(t("footer.ionos.eyebrow"))}
              </p>
              <h4 className="font-display font-semibold text-sm text-navy-foreground mb-2">
                {String(t("footer.ionos.title"))}
              </h4>
              <p className="text-xs opacity-70 leading-relaxed max-w-2xl">
                {String(t("footer.ionos.description"))}
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-border/20 mt-8 pt-6 text-center text-xs opacity-40">
          © {new Date().getFullYear()} Resilience365. {String(t("footer.allRights"))}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
